import { useState, useRef, useEffect, useLayoutEffect } from 'react'
import { ChevronDown, Menu, X } from 'lucide-react'
import { Link, useLocation } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { siteConfig } from '@/config/site'
import logoUrl from '@/assets/images/Codefied..svg'
import {
  MegaMenu,
  MegaMenuMobile,
  ProjectsMenu,
  ProjectsMenuMobile,
  CompanyMenu,
  CompanyMenuMobile,
} from './MegaMenu'

export function Header() {
  const [mobileOpen, setMobileOpen] = useState(false)
  const [mobileSuiteOpen, setMobileSuiteOpen] = useState(false)
  const [mobileProjectsOpen, setMobileProjectsOpen] = useState(false)
  const [mobileCompanyOpen, setMobileCompanyOpen] = useState(false)
  const [megaMenuOpen, setMegaMenuOpen] = useState(false)
  const [projectsMenuOpen, setProjectsMenuOpen] = useState(false)
  const [companyMenuOpen, setCompanyMenuOpen] = useState(false)
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const projectsCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const companyCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const projectsTriggerRef = useRef<HTMLDivElement>(null)
  const companyTriggerRef = useRef<HTMLDivElement>(null)

  const [projectsLeft, setProjectsLeft] = useState<number | 'auto'>('auto')
  const [companyLeft, setCompanyLeft] = useState<number | 'auto'>('auto')

  const [scrolled, setScrolled] = useState(false)
  const { pathname } = useLocation()
  const isHome = pathname === '/' || pathname === '/home'

  useLayoutEffect(() => {
    const updatePositions = () => {
      if (projectsTriggerRef.current) {
        const rect = projectsTriggerRef.current.getBoundingClientRect()
        setProjectsLeft(rect.left + projectsTriggerRef.current.offsetWidth / 2 - 326)
      }
      if (companyTriggerRef.current) {
        const rect = companyTriggerRef.current.getBoundingClientRect()
        setCompanyLeft(rect.left + companyTriggerRef.current.offsetWidth / 2 - 200)
      }
    }

    updatePositions()
    window.addEventListener('resize', updatePositions)
    return () => window.removeEventListener('resize', updatePositions)
  }, [projectsMenuOpen, companyMenuOpen, scrolled])

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    // Intentionally reset menu state on route change.
    /* eslint-disable react-hooks/set-state-in-effect */
    setMegaMenuOpen(false)
    setProjectsMenuOpen(false)
    setCompanyMenuOpen(false)
    /* eslint-enable react-hooks/set-state-in-effect */
  }, [pathname])

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden'
    } else if (megaMenuOpen || projectsMenuOpen || companyMenuOpen) {
      document.body.style.overflow = 'hidden'
      document.body.style.paddingRight = `${window.innerWidth - document.documentElement.clientWidth}px`
      document.documentElement.style.scrollbarGutter = 'stable'
      document.body.style.scrollbarWidth = 'auto'
    } else {
      document.body.style.overflow = ''
      document.body.style.paddingRight = ''
      document.documentElement.style.scrollbarGutter = ''
    }
    return () => {
      document.body.style.overflow = ''
      document.body.style.paddingRight = ''
      document.documentElement.style.scrollbarGutter = ''
    }
  }, [megaMenuOpen, projectsMenuOpen, companyMenuOpen, mobileOpen])

  const closeMobileNav = () => {
    setMobileOpen(false)
    setMobileSuiteOpen(false)
    setMobileProjectsOpen(false)
    setMobileCompanyOpen(false)
  }

  const openMegaMenu = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current)
    setProjectsMenuOpen(false)
    setCompanyMenuOpen(false)
    setMegaMenuOpen(true)
  }

  const closeMegaMenu = () => {
    closeTimer.current = setTimeout(() => setMegaMenuOpen(false), 200)
  }

  const openProjectsMenu = () => {
    if (projectsCloseTimer.current) clearTimeout(projectsCloseTimer.current)
    setMegaMenuOpen(false)
    setCompanyMenuOpen(false)
    setProjectsMenuOpen(true)
  }

  const closeProjectsMenu = () => {
    projectsCloseTimer.current = setTimeout(() => setProjectsMenuOpen(false), 200)
  }

  const openCompanyMenu = () => {
    if (companyCloseTimer.current) clearTimeout(companyCloseTimer.current)
    setMegaMenuOpen(false)
    setProjectsMenuOpen(false)
    setCompanyMenuOpen(true)
  }

  const closeCompanyMenu = () => {
    companyCloseTimer.current = setTimeout(() => setCompanyMenuOpen(false), 200)
  }

  return (
    <header
      className={cn(
        'fixed inset-x-0 top-0 z-50 transition-all duration-300',
        scrolled
          ? 'bg-white/70 shadow-[0_4px_24px_rgba(0,0,0,0.06)] backdrop-blur-xl backdrop-saturate-150'
          : isHome
            ? 'bg-white/5 backdrop-blur-[4px] backdrop-saturate-150'
            : 'bg-transparent',
      )}
    >
      <div className="container-site">
        <div className="flex items-center justify-between py-4 lg:h-[80px]">
          {/* Logo */}
          <Link to="/" className="flex shrink-0 items-center" aria-label="Codefied home">
            <img src={logoUrl} alt="Codefied Logo" width="126" height="24" className="w-auto" />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden items-center gap-1 lg:flex" aria-label="Primary navigation">
            {siteConfig.nav.map((item) => {
              const isCompany = item.label === 'Company'
              const isProjects = item.label === 'Products'
              const isSuite = 'hasDropdown' in item && !isCompany && !isProjects
              return (
                <div
                  key={item.label}
                  ref={isProjects ? projectsTriggerRef : isCompany ? companyTriggerRef : undefined}
                  className="relative"
                  onMouseEnter={
                    isSuite
                      ? openMegaMenu
                      : isProjects
                        ? openProjectsMenu
                        : isCompany
                          ? openCompanyMenu
                          : undefined
                  }
                  onMouseLeave={
                    isSuite
                      ? closeMegaMenu
                      : isProjects
                        ? closeProjectsMenu
                        : isCompany
                          ? closeCompanyMenu
                          : undefined
                  }
                >
                  {isProjects ? (
                    <span className="nav-item-header flex cursor-default items-center gap-0.5 rounded-md px-3 py-1.5">
                      {item.label}
                      <ChevronDown
                        size={13}
                        className="mt-px opacity-60"
                        style={{
                          transform: projectsMenuOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 300ms ease-in-out',
                        }}
                        aria-hidden
                      />
                    </span>
                  ) : (
                    <a
                      href={item.href}
                      className={cn(
                        'nav-item-header flex items-center gap-0.5 rounded-md px-3 py-1.5',
                        pathname === item.href && 'active',
                      )}
                    >
                      {item.label}
                      {'hasDropdown' in item && item.hasDropdown && (
                        <ChevronDown
                          size={13}
                          className="mt-px opacity-60"
                          style={{
                            transform:
                              (isSuite && megaMenuOpen) || (isCompany && companyMenuOpen)
                                ? 'rotate(180deg)'
                                : 'rotate(0deg)',
                            transition: 'transform 300ms ease-in-out',
                          }}
                          aria-hidden
                        />
                      )}
                    </a>
                  )}

                  {/* Invisible bridge to prevent gap between nav item and dropdown panel */}
                  {(isSuite || isProjects || isCompany) && (
                    <div className="absolute -bottom-6 left-1/2 h-6 w-48 -translate-x-1/2" />
                  )}
                </div>
              )
            })}
          </nav>

          {/* CTA */}
          <div className="hidden lg:flex">
            <Link
              to="/contact"
              className="flex items-center justify-center gap-[8.772px] rounded-[8.772px] bg-black px-[17.544px] py-[12px] text-center text-[18px] font-medium leading-normal text-white transition-all duration-200 hover:bg-brand-gray-900 hover:shadow-sm active:scale-[0.98]"
              style={
                {
                  leadingTrim: 'both',
                  textEdge: 'cap',
                  border: '0.877px solid rgba(255, 255, 255, 0.09)',
                } as React.CSSProperties
              }
            >
              Get Started
            </Link>
          </div>

          {/* Mobile toggle */}
          <button
            className="flex size-8 items-center justify-center rounded-md text-brand-gray-600 hover:bg-brand-gray-100 lg:hidden"
            onClick={() => setMobileOpen((o) => !o)}
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {/* MegaMenu — 1360×573 panel, centered under header (fixed to viewport, not full-bleed) */}
      <div
        onMouseEnter={openMegaMenu}
        onMouseLeave={closeMegaMenu}
        className={cn(
          'fixed left-1/2 top-[80px] z-40 hidden h-[573px] max-h-[calc(100dvh-88px)] w-[1360px] max-w-[calc(100vw-32px)] min-h-0 transition-all duration-200 ease-out lg:block',
          megaMenuOpen ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0',
        )}
        style={{
          transform: megaMenuOpen
            ? 'translateX(-50%) translateY(0)'
            : 'translateX(-50%) translateY(0.5rem)',
        }}
      >
        <MegaMenu />
      </div>

      {/* Projects dropdown — anchored under the Projects nav item */}
      <div
        onMouseEnter={openProjectsMenu}
        onMouseLeave={closeProjectsMenu}
        className={cn(
          'fixed top-[80px] z-40 hidden transition-all duration-200 ease-out lg:block',
          projectsMenuOpen ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0',
        )}
        style={{
          left: projectsLeft,
          transform: projectsMenuOpen ? 'translateY(0)' : 'translateY(0.5rem)',
        }}
      >
        <ProjectsMenu />
      </div>

      {/* Company dropdown — anchored under the Company nav item */}
      <div
        onMouseEnter={openCompanyMenu}
        onMouseLeave={closeCompanyMenu}
        className={cn(
          'fixed top-[80px] z-40 hidden transition-all duration-200 ease-out lg:block',
          companyMenuOpen ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0',
        )}
        style={{
          left: companyLeft,
          transform: companyMenuOpen ? 'translateY(0)' : 'translateY(0.5rem)',
        }}
      >
        <CompanyMenu />
      </div>



      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="scrollbar-hide max-h-[calc(100dvh-72px)] overflow-y-auto border-t border-brand-gray-100 bg-white px-6 pb-6 pt-4 lg:hidden">
          <nav className="flex flex-col gap-1" aria-label="Mobile navigation">
            {siteConfig.nav.map((item) => {
              const isCompany = item.label === 'Company'
              const isProjects = item.label === 'Products'
              const isSuite = 'hasDropdown' in item && !isCompany && !isProjects

              if (isSuite || isProjects || isCompany) {
                const open = isSuite
                  ? mobileSuiteOpen
                  : isProjects
                    ? mobileProjectsOpen
                    : mobileCompanyOpen
                const setOpen = isSuite
                  ? setMobileSuiteOpen
                  : isProjects
                    ? setMobileProjectsOpen
                    : setMobileCompanyOpen

                return (
                  <div key={item.label}>
                    <button
                      type="button"
                      className="flex w-full items-center justify-between rounded-md px-2 py-2 text-left text-sm text-brand-gray-600 transition-colors hover:text-brand-black"
                      onClick={() => setOpen((prev) => !prev)}
                      aria-expanded={open}
                    >
                      <span>{item.label}</span>
                      <ChevronDown
                        size={13}
                        className={cn(
                          'shrink-0 opacity-60 transition-transform duration-200',
                          open && 'rotate-180',
                        )}
                        aria-hidden
                      />
                    </button>
                    {open &&
                      (isSuite ? (
                        <MegaMenuMobile onNavigate={closeMobileNav} />
                      ) : isProjects ? (
                        <ProjectsMenuMobile onNavigate={closeMobileNav} />
                      ) : (
                        <CompanyMenuMobile onNavigate={closeMobileNav} />
                      ))}
                  </div>
                )
              }

              return (
                <a
                  key={item.label}
                  href={item.href}
                  className="flex items-center gap-1 rounded-md px-2 py-2 text-sm text-brand-gray-600 transition-colors hover:text-brand-black"
                  onClick={closeMobileNav}
                >
                  {item.label}
                  {'hasDropdown' in item && item.hasDropdown ? (
                    <ChevronDown size={13} className="opacity-60" aria-hidden />
                  ) : null}
                </a>
              )
            })}
          </nav>
          <div className="mt-5">
            <Link
              to="/contact"
              onClick={closeMobileNav}
              className="flex w-full items-center justify-center rounded-full bg-brand-black py-2.5 text-sm font-semibold text-white"
            >
              Get Started
            </Link>
          </div>
        </div>
      )}
    </header>
  )
}
